import { match } from 'ts-pattern';

import {
  ClientPortalStepTypeEnum,
  extractMappingFromKey,
  PropertyMappingEntityEnum,
} from '@dotfile/shared/domain';

import {
  FormDatastoreCase,
  FormDatastoreCompany,
  FormDatastoreIndividual,
} from '../context/datastore/datastore';
import { FieldsValues } from '../utils';
import {
  LogicDataContext,
  normalizeDataToLogicContext,
  sanitizeValues,
} from './build-step-logic-data-context';

export type BuildBlocksLogicDataContextParams = {
  /**
   * Default data of the form
   * `company` will be put in `affiliated_company` or `main_company` depending on the step type
   */
  defaultData: {
    case?: FormDatastoreCase | undefined;
    company?: FormDatastoreCompany;
    individual?: FormDatastoreIndividual;
  };
  /**
   * Current value of the form
   */
  formValues: FieldsValues;
  /**
   * Type of the step of the form
   */
  stepType: ClientPortalStepTypeEnum;
};

export function buildBlocksLogicDataContext({
  defaultData,
  formValues,
  stepType,
}: BuildBlocksLogicDataContextParams): LogicDataContext {
  const logicDataContext: LogicDataContext = {};

  // Get from default data
  match(stepType)
    .with(ClientPortalStepTypeEnum.affiliated_companies_edit, () => {
      // Get selected affiliated company
      logicDataContext.affiliated_company = normalizeDataToLogicContext(
        PropertyMappingEntityEnum.company,
        defaultData.company,
      );
    })
    .with(ClientPortalStepTypeEnum.individuals_edit, () => {
      // Get selected individual
      logicDataContext.individual = normalizeDataToLogicContext(
        PropertyMappingEntityEnum.individual,
        defaultData.individual,
      );
    })
    .otherwise(() => {
      // Ignores default data for other step types
    });

  // Map form values
  Object.entries(formValues).forEach(([key, value]) => {
    const mapping = extractMappingFromKey(key);

    match(stepType)
      // KYB Only
      .with(ClientPortalStepTypeEnum.affiliated_companies_edit, () =>
        match(mapping.entity)
          .with(PropertyMappingEntityEnum.company, () => {
            logicDataContext.affiliated_company = {
              ...logicDataContext.affiliated_company,
              [key]: sanitizeValues(mapping, value),
            };
          })
          .otherwise(() => {
            // Ignore other entities, should not happen in affiliated_companies_edit step for KYB
          }),
      )
      .with(ClientPortalStepTypeEnum.individuals_edit, () =>
        match(mapping.entity)
          .with(PropertyMappingEntityEnum.individual, () => {
            logicDataContext.individual = {
              ...logicDataContext.individual,
              [key]: sanitizeValues(mapping, value),
            };
          })
          .otherwise(() => {
            // Ignore other entities, should not happen in affiliated_companies_edit step for KYB
          }),
      )

      // Common for all step / client portal type
      .otherwise(() =>
        match(mapping.entity)
          .with(PropertyMappingEntityEnum.case, () => {
            logicDataContext.case = {
              ...logicDataContext.case,
              [key]: sanitizeValues(mapping, value),
            };
          })
          // For KYB only, in custom / company_edit step
          .with(PropertyMappingEntityEnum.company, () => {
            logicDataContext.main_company = {
              ...logicDataContext.main_company,
              [key]: sanitizeValues(mapping, value),
            };
          })
          // For KYC only, in custom / individual_edit step
          .with(PropertyMappingEntityEnum.individual, () => {
            logicDataContext.individual = {
              ...logicDataContext.individual,
              [key]: sanitizeValues(mapping, value),
            };
          })
          .exhaustive(),
      );
  });

  return logicDataContext;
}
